// Browser-side conversions for the ChatGPT Ads Measurement Pixel (`oaiq`).
//
// The pixel itself is loaded by AnalyticsScripts when
// NEXT_PUBLIC_OPENAI_ADS_PIXEL_ID is set. Its loader installs a queueing stub
// on window.oaiq straight away, so calls made here before the SDK finishes
// downloading are replayed once it arrives. When the pixel is not configured
// window.oaiq never exists and every call below is a no-op.
//
// Every event carries a deterministic event_id so a later server-side
// Conversions API send for the same action is deduplicated, not double counted.

const debugEnabled = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === "true";
const shouldTrack = process.env.NODE_ENV === "production" || debugEnabled;

const PURCHASE_STORAGE_PREFIX = "hyred.openai_ads_purchase.";
const CHECKOUT_STORAGE_PREFIX = "hyred.openai_ads_checkout.";

// OpenAI wants amounts in the currency's ISO 4217 minor unit. IDR has an
// exponent of 2, so Rp79.000 is sent as 7900000.
export const IDR_MINOR_UNITS = 100;

// Purchases use the standard `order_created` event, not a custom one: a
// campaign bidding on conversions can only optimize for a standard event. The
// server-side Conversions API send (openai-ads-server.ts) uses the same name
// and id, so the pixel and server copies of one purchase deduplicate.
export const OPENAI_ADS_PURCHASE_EVENT = "order_created";

export function openAiAdsPurchaseEventId(paymentId: string) {
  return `${OPENAI_ADS_PURCHASE_EVENT}_${paymentId}`;
}

export type OpenAiAdsEventName =
  | "order_created"
  | "checkout_started"
  | "registration_completed";

export interface OpenAiAdsEventData {
  type: "contents" | "customer_action";
  amount?: number;
  currency?: string;
  contents?: Array<{
    id: string;
    name?: string;
    content_type?: string;
    quantity?: number;
  }>;
}

export function trackOpenAiAdsEvent(
  eventName: OpenAiAdsEventName,
  data: OpenAiAdsEventData,
  eventId: string,
): boolean {
  if (typeof window === "undefined" || !shouldTrack) {
    return false;
  }

  if (typeof window.oaiq !== "function") {
    return false;
  }

  if (debugEnabled) {
    console.log("[analytics] oaiq", eventName, data, eventId);
  }

  window.oaiq("measure", eventName, data, { event_id: eventId });
  return true;
}

/** One tier bought once, in the shape checkout_started and order_created share. */
export function openAiAdsContentsData(
  amount: number,
  tier: string | null | undefined,
): OpenAiAdsEventData {
  const product = tier ?? "unknown";

  return {
    type: "contents",
    amount: Math.round(amount * IDR_MINOR_UNITS),
    currency: "IDR",
    contents: [{ id: product, name: product, content_type: "product", quantity: 1 }],
  };
}

export interface OpenAiAdsPurchase {
  paymentId: string;
  amount: number | null | undefined;
  tier?: string | null;
}

/**
 * Sends `order_created` at most once per payment in this browser.
 * The success screen can render again (polling, reload, the redirect after a
 * hosted checkout), and each send would be another conversion. Zero-value
 * completions (free vouchers) are skipped so only real revenue reaches the ad
 * platform.
 */
export function trackOpenAiAdsPurchase({
  paymentId,
  amount,
  tier,
}: OpenAiAdsPurchase): boolean {
  return sendOncePerPayment(PURCHASE_STORAGE_PREFIX, paymentId, amount, () =>
    trackOpenAiAdsEvent(
      OPENAI_ADS_PURCHASE_EVENT,
      openAiAdsContentsData(amount ?? 0, tier),
      openAiAdsPurchaseEventId(paymentId),
    ),
  );
}

/**
 * Sends `checkout_started` once per payment, when the buyer is shown a way to
 * pay. Same zero-value rule as the purchase.
 */
export function trackOpenAiAdsCheckoutStarted({
  paymentId,
  amount,
  tier,
}: OpenAiAdsPurchase): boolean {
  return sendOncePerPayment(CHECKOUT_STORAGE_PREFIX, paymentId, amount, () =>
    trackOpenAiAdsEvent(
      "checkout_started",
      openAiAdsContentsData(amount ?? 0, tier),
      `checkout_started_${paymentId}`,
    ),
  );
}

function sendOncePerPayment(
  storagePrefix: string,
  paymentId: string,
  amount: number | null | undefined,
  send: () => boolean,
): boolean {
  if (typeof window === "undefined" || !shouldTrack) {
    return false;
  }

  if (!paymentId || typeof amount !== "number" || !(amount > 0)) {
    return false;
  }

  const storageKey = `${storagePrefix}${paymentId}`;

  if (readStorage(storageKey)) {
    return false;
  }

  const sent = send();

  if (sent) {
    writeStorage(storageKey);
  }

  return sent;
}

export function trackOpenAiAdsRegistration(userId: string): boolean {
  if (!userId) {
    return false;
  }

  return trackOpenAiAdsEvent(
    "registration_completed",
    { type: "customer_action" },
    `registration_completed_${userId}`,
  );
}

function readStorage(key: string) {
  try {
    return window.localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function writeStorage(key: string) {
  try {
    window.localStorage.setItem(key, new Date().toISOString());
  } catch {
    // Storage blocked: the caller's in-page latch still prevents repeats
    // within this visit.
  }
}
